import { useEffect, useMemo, useRef, useState } from "react";
import type { TicketCatalog, TicketStatus } from "../types";

type TicketFilter = TicketStatus | "all";

const statusLabels: Record<TicketStatus, string> = {
  active: "Active",
  waiting: "Waiting",
  closed: "Closed",
};

const statusOrder: Record<TicketStatus, number> = {
  active: 0,
  waiting: 1,
  closed: 2,
};

function normalizeSearch(value: string) {
  return value.normalize("NFKC").toLocaleLowerCase("ja");
}

export function filterTicketCatalog(tickets: TicketCatalog[], query: string, status: TicketFilter) {
  const terms = normalizeSearch(query).trim().split(/\s+/).filter(Boolean);
  return [...tickets]
    .filter((ticket) => {
      if (status !== "all" && ticket.status !== status) return false;
      if (!terms.length) return true;
      const searchable = normalizeSearch([
        ticket.title,
        ticket.ticket_key,
        ticket.project,
        ticket.area,
        ticket.type,
      ].join(" "));
      return terms.every((term) => searchable.includes(term));
    })
    .sort((a, b) => statusOrder[a.status] - statusOrder[b.status] || a.title.localeCompare(b.title, "ja"));
}

export function TicketPicker({ tickets, value, onChange, disabled = false }: {
  tickets: TicketCatalog[];
  value: string;
  onChange: (ticketKey: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<TicketFilter>("active");
  const searchRef = useRef<HTMLInputElement | null>(null);
  const selected = tickets.find((ticket) => ticket.ticket_key === value);
  const visibleTickets = useMemo(() => filterTicketCatalog(tickets, query, status), [tickets, query, status]);
  const counts = useMemo(() => ({
    all: tickets.length,
    active: tickets.filter((ticket) => ticket.status === "active").length,
    waiting: tickets.filter((ticket) => ticket.status === "waiting").length,
    closed: tickets.filter((ticket) => ticket.status === "closed").length,
  }), [tickets]);

  function showPicker() {
    if (disabled) return;
    setQuery("");
    setStatus(selected?.status ?? "active");
    setOpen(true);
  }

  function choose(ticketKey: string) {
    onChange(ticketKey);
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  return <div className="ticket-picker">
    <button
      type="button"
      className="ticket-picker-trigger"
      disabled={disabled}
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={showPicker}
    >
      <span className="ticket-picker-trigger-copy">
        {selected && <span className={`ticket-status ticket-status-${selected.status}`}>{statusLabels[selected.status]}</span>}
        <span className={selected ? "" : "ticket-picker-placeholder"}>{selected?.title ?? "関連チケットなし"}</span>
      </span>
      <span className="ticket-picker-chevron" aria-hidden="true">⌄</span>
    </button>
    {open && <>
      <button
        type="button"
        className="ticket-picker-dismiss"
        aria-label="関連チケット選択を閉じる"
        onClick={() => setOpen(false)}
        onWheel={(event) => { event.preventDefault(); event.stopPropagation(); }}
        onTouchMove={(event) => { event.preventDefault(); event.stopPropagation(); }}
      />
      <section
        className="ticket-picker-panel"
        role="dialog"
        aria-modal="true"
        aria-label="関連チケットを選択"
        onWheel={(event) => event.stopPropagation()}
        onTouchMove={(event) => event.stopPropagation()}
      >
        <header className="ticket-picker-header">
          <div><p className="eyebrow">RELATED TICKET</p><h3>関連チケットを選択</h3></div>
          <button type="button" className="icon-button" aria-label="閉じる" onClick={() => setOpen(false)}>×</button>
        </header>
        <div className="ticket-picker-search">
          <span aria-hidden="true">⌕</span>
          <input ref={searchRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="タイトル・キー・プロジェクトで検索" aria-label="関連チケットを検索" />
          {query && <button type="button" aria-label="検索を消去" onClick={() => setQuery("")}>×</button>}
        </div>
        <div className="ticket-picker-tabs" role="group" aria-label="ステータスで絞り込む">
          {(["all", "active", "waiting", "closed"] as TicketFilter[]).map((item) => <button
            type="button"
            key={item}
            className={status === item ? "selected" : ""}
            aria-pressed={status === item}
            onClick={() => setStatus(item)}
          >{item === "all" ? "すべて" : statusLabels[item]}<span>{counts[item]}</span></button>)}
        </div>
        <div className="ticket-picker-list">
          <button type="button" className={`ticket-picker-item ticket-picker-none ${!value ? "selected" : ""}`} aria-pressed={!value} onClick={() => choose("")}>
            <span className="ticket-picker-none-icon" aria-hidden="true">—</span>
            <span><strong>関連チケットなし</strong><small>紐づけを解除します</small></span>
            {!value && <span className="ticket-picker-check" aria-hidden="true">✓</span>}
          </button>
          {visibleTickets.map((ticket) => <button
            type="button"
            key={ticket.ticket_key}
            className={`ticket-picker-item ${ticket.ticket_key === value ? "selected" : ""}`}
            aria-pressed={ticket.ticket_key === value}
            onClick={() => choose(ticket.ticket_key)}
          >
            <span className={`ticket-status ticket-status-${ticket.status}`}>{statusLabels[ticket.status]}</span>
            <span className="ticket-picker-item-copy"><strong>{ticket.title}</strong><small>{[ticket.project, ticket.area, ticket.type].filter(Boolean).join(" · ")}</small></span>
            {ticket.ticket_key === value && <span className="ticket-picker-check" aria-hidden="true">✓</span>}
          </button>)}
          {!visibleTickets.length && <div className="ticket-picker-empty"><strong>該当するチケットがありません</strong><span>検索語かステータスを変更してください。</span></div>}
        </div>
      </section>
    </>}
  </div>;
}
